import React from "react";
import styled from "styled-components";

export interface ConfidenceIntervalBarProps {
  /** Wilson lower bound, on the same already-scaled-by-100 percent scale as `conversionRate`. */
  low: number;
  /** Wilson upper bound. */
  high: number;
  /** The point estimate, drawn as a tick inside the interval. */
  value: number;
  /**
   * `[min, max]` of the axis this bar is drawn against. Shared by every bar in a comparison so that
   * two intervals can be read against each other — an independently-scaled bar would make two very
   * different intervals look identical (see design.md, Decision 3).
   */
  domain: [number, number];
  color: string;
  testId?: string;
}

const Track = styled.div`
  position: relative;
  height: 8px;
  border-radius: 4px;
  background-color: #f0f0f0;
  width: 100%;
`;

const Interval = styled.div<{ $color: string }>`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 4px;
  background-color: ${({ $color }) => $color};
  opacity: 0.35;
`;

const PointMarker = styled.div<{ $color: string }>`
  position: absolute;
  top: -2px;
  width: 2px;
  height: 12px;
  background-color: ${({ $color }) => $color};
`;

/** Percentage position of `value` inside `domain`, clamped to the track and safe on a zero-width
 *  domain (both bounds identical — everything collapses to the centre rather than dividing by 0). */
function positionIn(domain: [number, number], value: number): number {
  const [min, max] = domain;
  const span = max - min;
  if (!Number.isFinite(span) || span <= 0) {
    return 50;
  }
  return Math.min(Math.max(((value - min) / span) * 100, 0), 100);
}

/**
 * A Wilson confidence interval drawn as a horizontal bar: the shaded span is the interval, the tick
 * is the point estimate. Purely presentational — every number it draws was computed by
 * `backoffice-manager`.
 */
export function ConfidenceIntervalBar({
  low,
  high,
  value,
  domain,
  color,
  testId,
}: ConfidenceIntervalBarProps) {
  const leftPercent = positionIn(domain, Math.min(low, high));
  const rightPercent = positionIn(domain, Math.max(low, high));
  // A degenerate interval still needs to be visible, hence the 1% floor on its width.
  const widthPercent = Math.max(rightPercent - leftPercent, 1);

  return (
    // Hidden from assistive technology on purpose: the caption rendered directly beneath it
    // already states the same interval in words, and announcing it twice is noise, not detail.
    <Track aria-hidden="true" data-testid={testId}>
      <Interval
        $color={color}
        style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
        data-testid={testId ? `${testId}-interval` : undefined}
      />
      <PointMarker
        $color={color}
        style={{ left: `${positionIn(domain, value)}%` }}
        data-testid={testId ? `${testId}-point` : undefined}
      />
    </Track>
  );
}
